import {
  LocationConditionRequest,
  LocationSettings,
  LocationConditionEvaluationTree,
  LocationConditionGroupNode,
  BrowserLanguageEvaluationLeaf,
  DeviceTypeEvaluationLeaf,
  PageValueEvaluationLeaf,
  QueryValueEvaluationLeaf,
  UrlValueEvaluationLeaf,
  BrowserLanguage,
  DeviceType,
} from '@chilipiper/api-client/src/chat-platform-chat'
import { LocationData } from '../types'

const compareStringRule = (
  operator: 'contains' | 'doesNotContain' | '=' | '!=' | 'isAnyOf' | 'isNotAnyOf' | 'equals',
  ruleValue = '',
  value = ''
) => {
  switch (operator) {
    case 'contains':
      return value.includes(ruleValue)
    case 'doesNotContain':
      return !value.includes(ruleValue)
    case 'isAnyOf':
    case 'equals':
    case '=':
      return value === ruleValue
    case 'isNotAnyOf':
    case '!=':
      return value !== ruleValue
    default:
      throw new Error(`Unknown rule operator: ${operator}`)
  }
}

const compareNumberRule = (
  operator: '=' | '>' | '<' | '!=' | '<=' | '>=',
  ruleValue: number,
  value: number
) => {
  switch (operator) {
    case '=':
      return value === ruleValue
    case '>':
      return value > ruleValue
    case '<':
      return value < ruleValue
    case '!=':
      return value !== ruleValue
    case '<=':
      return value <= ruleValue
    case '>=':
      return value >= ruleValue
    default:
      throw new Error(`Unknown rule operator: ${operator}`)
  }
}

export const processPlaybooksLocationSettings = (
  locationSettings: Record<string, LocationSettings>,
  locationData: LocationData
): {
  playbookIds: string[]
  locationEvaluation: Record<string, LocationConditionEvaluationTree>
} => {
  return Object.entries(locationSettings).reduce(
    (acc, [playbookId, locationRules]) => {
      if (locationRules.type === 'ShowToEveryone') {
        acc.playbookIds.push(playbookId)
      } else {
        const evaluation = processGroup(locationRules.rules, locationData)
        if (evaluation.evaluatedTo) {
          acc.playbookIds.push(playbookId)
        }
        acc.locationEvaluation[playbookId] = evaluation
      }
      return acc
    },
    { playbookIds: [], locationEvaluation: {} } as {
      playbookIds: string[]
      locationEvaluation: Record<string, LocationConditionEvaluationTree>
    }
  )
}

const removeHttpPrefix = (url: string) => {
  return url.replace(/^(?:https?:\/\/)?(?:www\.)?/, '').replace(/\/$/, '')
}

const processGroup = (
  rule: LocationConditionRequest,
  locationData: LocationData
): LocationConditionEvaluationTree => {
  const [originUrl, originQueryParams = ''] = locationData.originUrl.split('?') as [
    string,
    ...Array<string>,
  ]

  switch (rule.type) {
    case 'UrlValueCondition': {
      const evaluatedTo = compareStringRule(
        rule.operator,
        removeHttpPrefix(rule.value.value),
        removeHttpPrefix(originUrl)
      )
      return {
        type: 'UrlValueEvaluationLeaf',
        conditionId: rule.conditionId,
        operator: rule.operator,
        expectedValue: { value: rule.value.value },
        actualValue: { value: originUrl },
        evaluatedTo,
      } as UrlValueEvaluationLeaf
    }
    case 'QueryValueCondition': {
      const evaluatedTo = compareStringRule(rule.operator, rule.value.value, originQueryParams)
      return {
        type: 'QueryValueEvaluationLeaf',
        conditionId: rule.conditionId,
        operator: rule.operator,
        expectedValue: { value: rule.value.value },
        actualValue: { value: originQueryParams },
        evaluatedTo,
      } as QueryValueEvaluationLeaf
    }
    case 'PageValueCondition': {
      let evaluatedTo: boolean
      let expectedValue: any
      let actualValue: any

      if (rule.value.type === 'PageScrollValue') {
        evaluatedTo = compareNumberRule(
          rule.operator,
          Math.floor(rule.value.value),
          Math.floor(locationData.maxScrollPercentage)
        )
        expectedValue = { type: 'PageScrollValue', value: Math.floor(rule.value.value) }
        actualValue = {
          type: 'PageScrollValue',
          value: Math.floor(locationData.maxScrollPercentage),
        }
      } else {
        const [time, unit] = rule.value.duration.split(' ') as [string, ...Array<string>]
        let timeInSeconds = 0
        if (unit === 'seconds') {
          timeInSeconds = parseInt(time)
        }
        if (unit === 'minutes') {
          timeInSeconds = parseInt(time) * 60
        }
        if (unit === 'hours') {
          timeInSeconds = parseInt(time) * 3600
        }
        evaluatedTo = compareNumberRule(rule.operator, timeInSeconds, locationData.userTimeOnPage)
        expectedValue = { type: 'TimeOnPageValue', duration: rule.value.duration }
        actualValue = {
          type: 'TimeOnPageValue',
          duration: `${locationData.userTimeOnPage} seconds`,
        }
      }

      return {
        type: 'PageValueEvaluationLeaf',
        conditionId: rule.conditionId,
        operator: rule.operator,
        expectedValue,
        actualValue,
        evaluatedTo,
      } as PageValueEvaluationLeaf
    }
    case 'LocationConditionGroup': {
      const children = rule.conditions.map(condition => processGroup(condition, locationData))
      const evaluatedTo =
        rule.operator === 'and'
          ? children.every(child => child.evaluatedTo)
          : children.some(child => child.evaluatedTo)

      return {
        type: 'LocationConditionGroupNode',
        id: rule.id,
        operator: rule.operator,
        children,
        evaluatedTo,
      } as LocationConditionGroupNode
    }
    case 'DeviceType': {
      const evaluatedTo =
        rule.operator === 'isAnyOf'
          ? isAnyOf(rule, locationData.deviceType)
          : isEveryOf(rule, locationData.deviceType)

      return {
        type: 'DeviceTypeEvaluationLeaf',
        conditionId: rule.conditionId,
        operator: rule.operator,
        expectedValues: rule.values,
        actualValue: locationData.deviceType,
        evaluatedTo,
      } as DeviceTypeEvaluationLeaf
    }
    case 'BrowserLanguage': {
      const evaluatedTo =
        rule.operator === 'isAnyOf'
          ? isAnyOf(rule, locationData.browserLanguage)
          : isEveryOf(rule, locationData.browserLanguage)

      return {
        type: 'BrowserLanguageEvaluationLeaf',
        conditionId: rule.conditionId,
        operator: rule.operator,
        expectedValues: rule.values,
        actualValue: locationData.browserLanguage,
        evaluatedTo,
      } as BrowserLanguageEvaluationLeaf
    }
  }
}

const isAnyOf = (rule: BrowserLanguage | DeviceType, actualValue: string) => {
  return rule.values?.some(ruleValue => compareStringRule(rule.operator, ruleValue, actualValue))
}

const isEveryOf = (rule: BrowserLanguage | DeviceType, actualValue: string) => {
  return rule.values?.every(ruleValue => compareStringRule(rule.operator, ruleValue, actualValue))
}
